import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, MapPin, ShieldCheck, Users, Baby, Timer, Flag, Smartphone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { mediaUrl } from "@/lib/api";
import Reveal from "@/components/Reveal";

const METRICS = [
  { icon: Users, label: "2 à 6 joueurs" },
  { icon: Baby, label: "Dès 6 ans" },
  { icon: Timer, label: "45 minutes" },
  { icon: Flag, label: "Fabriqué en France" },
  { icon: Smartphone, label: "App compagnon incluse" },
];

export default function Hero() {
  const { content } = useSite();
  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-cream">
      <div className="container-x pt-10 pb-16 sm:pt-16 sm:pb-24 lg:pt-20 lg:pb-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <motion.div className="lg:col-span-7"
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}>
            <p className="eyebrow mb-5" data-testid="hero-eyebrow">{content.hero_eyebrow || "Partenaire OGC Nice — Édition Nice"}</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1.05] text-onyx" data-testid="hero-title">
              {content.hero_title || "Le jeu qui fait vivre Nice et ses Aiglons"}
            </h1>
            <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl" data-testid="hero-subtitle">
              {content.hero_subtitle}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/acheter" data-testid="hero-cta-acheter"
                className="inline-flex justify-center items-center bg-rouge text-white px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-rouge-hover hover:-translate-y-0.5 transition-all">
                Acheter le jeu
              </Link>
              <Link to="/jeu" data-testid="hero-cta-decouvrir"
                className="inline-flex justify-center items-center border-2 border-onyx text-onyx px-8 py-4 text-sm font-bold uppercase tracking-wide hover:bg-onyx hover:text-cream transition-colors">
                Découvrir le jeu
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs sm:text-sm text-muted-foreground font-medium">
              <span className="inline-flex items-center gap-1.5"><Truck className="w-4 h-4 text-rouge" /> Livraison 48h</span>
              <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4 text-rouge" /> Retrait gratuit à Nice</span>
              <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-rouge" /> Paiement sécurisé</span>
            </div>
          </motion.div>
          <motion.div className="lg:col-span-5 relative"
            initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
            <div className="absolute -inset-4 bg-rouge/5 rotate-2" aria-hidden="true" />
            <img src={mediaUrl("/api/uploads/produit-complet.png")} alt="Boîte et contenu du jeu Douzième Homme — Édition Nice"
              data-testid="hero-product-image"
              className="relative w-full max-w-md mx-auto lg:max-w-none drop-shadow-2xl" />
          </motion.div>
        </div>
      </div>
      <Reveal className="border-y border-line bg-white" y={0}>
        <div className="container-x py-5 flex flex-wrap justify-center sm:justify-between gap-x-8 gap-y-3" data-testid="metrics-strip">
          {METRICS.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 text-sm font-semibold text-onyx">
              <Icon className="w-4 h-4 text-rouge" /> {label}
            </span>
          ))}
        </div>
      </Reveal>
    </section>
  );
}
